import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'
import {
  PositionConfigInfo,
  Price,
  getInitializableTickIndex,
  getNextInitializableTickIndex,
  getPrevInitializableTickIndex,
} from '@kunalabs-io/kai'
import { useFocusWithin } from 'utils/useFocusWithin'

const NUMBERS_DECIMALS_REGEX = /^[0-9]*\.?[0-9]*$/

const toDisplayPrice = (
  price: Price<PhantomTypeArgument, PhantomTypeArgument>,
  isReversed: boolean
) =>
  isReversed
    ? price.inverted().human.toDecimalPlaces(8).toString()
    : price.human.toDecimalPlaces(8).toString()

type Pool = ReturnType<
  PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>['poolFromBcs']
>

const parseHumanPrice = (pool: Pool, price: string, isReversed: boolean) =>
  isReversed
    ? Price.fromHuman(pool.X, pool.Y, price).inverted()
    : Price.fromHuman(pool.X, pool.Y, price)

interface UsePriceRangeArgs {
  configInfo: PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>
  pool: Pool | undefined
  initialConfigId?: string | null
  initialMinPrice?: string | null
  initialMaxPrice?: string | null
}

export const usePriceRange = ({
  configInfo,
  pool,
  initialConfigId,
  initialMinPrice,
  initialMaxPrice,
}: UsePriceRangeArgs) => {
  const isReversed = configInfo.isReversedPair

  const [rangeMin, setRangeMin] = useState('')
  const [initialRangeMin, setInitialRangeMin] = useState<string>('')
  const [rangeMax, setRangeMax] = useState('')
  const [initialRangeMax, setInitialRangeMax] = useState<string>('')
  const [tickA, setTickA] = useState<number | undefined>()
  const [tickB, setTickB] = useState<number | undefined>()

  const initialCopyValuesUsed = useRef(false)

  // Initialize ticks from copy position URL params
  useEffect(() => {
    if (
      initialConfigId &&
      initialMinPrice &&
      initialMaxPrice &&
      pool &&
      !initialCopyValuesUsed.current
    ) {
      let [priceMin, tickIndexMin] = pool.priceToClosestInitializablePrice(
        parseHumanPrice(pool, initialMinPrice, isReversed)
      )
      if (tickB && tickIndexMin >= tickB) {
        tickIndexMin = getPrevInitializableTickIndex(tickB, pool.tickSpacing)
        priceMin = pool.tickToPrice(tickIndexMin)
      }
      if (isReversed) {
        setRangeMax(priceMin.inverted().human.toDecimalPlaces(8).toString())
        setTickB(tickIndexMin)
      } else {
        setRangeMin(priceMin.human.toDecimalPlaces(8).toString())
        setTickA(tickIndexMin)
      }

      // Handle range max
      let [priceMax, tickIndexMax] = pool.priceToClosestInitializablePrice(
        parseHumanPrice(pool, initialMaxPrice, isReversed)
      )
      if (tickA && tickIndexMax <= tickA) {
        tickIndexMax = getNextInitializableTickIndex(tickA, pool.tickSpacing)
        priceMax = pool.tickToPrice(tickIndexMax)
      }

      if (isReversed) {
        setRangeMin(priceMax.inverted().human.toDecimalPlaces(8).toString())
        setTickA(tickIndexMax)
      } else {
        setRangeMax(priceMax.human.toDecimalPlaces(8).toString())
        setTickB(tickIndexMax)
      }

      initialCopyValuesUsed.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfigId, initialMaxPrice, initialMinPrice, pool])

  // Input change handlers
  const handleRangeMinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (NUMBERS_DECIMALS_REGEX.test(value)) {
      setRangeMin(e.target.value)
    }
  }, [])

  const handleRangeMaxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (NUMBERS_DECIMALS_REGEX.test(value)) {
      setRangeMax(e.target.value)
    }
  }, [])

  // Focus-within: snap prices to closest initializable tick on blur
  const { ref: rangeMinRef } = useFocusWithin({
    onBlur: () => {
      if (!pool || !rangeMin) {
        setRangeMin('')
        setTickA(undefined)
        return
      }

      if (rangeMin === initialRangeMin) {
        return
      }

      let [price, tickIndex] = pool.priceToClosestInitializablePrice(
        parseHumanPrice(pool, rangeMin, isReversed)
      )
      if (tickB && tickIndex >= tickB) {
        tickIndex = getPrevInitializableTickIndex(tickB, pool.tickSpacing)
        price = pool.tickToPrice(tickIndex)
      }

      setRangeMin(toDisplayPrice(price, isReversed))
      setTickA(tickIndex)
    },
    onFocus: () => {
      setInitialRangeMin(rangeMin)
    },
  })

  const { ref: rangeMaxRef } = useFocusWithin({
    onBlur: () => {
      if (!pool || !rangeMax) {
        setRangeMax('')
        setTickB(undefined)
        return
      }

      if (rangeMax === initialRangeMax) {
        return
      }

      let [price, tickIndex] = pool.priceToClosestInitializablePrice(
        parseHumanPrice(pool, rangeMax, isReversed)
      )
      if (tickA && tickIndex <= tickA) {
        tickIndex = getNextInitializableTickIndex(tickA, pool.tickSpacing)
        price = pool.tickToPrice(tickIndex)
      }

      setRangeMax(toDisplayPrice(price, isReversed))
      setTickB(tickIndex)
    },
    onFocus: () => {
      setInitialRangeMax(rangeMax)
    },
  })

  // Tick error validation
  const tickAError = useMemo(() => {
    if (tickA === undefined || !pool) {
      return false
    }
    return tickA > pool.currentTick()
  }, [tickA, pool])

  const tickBError = useMemo(() => {
    if (tickB === undefined || !pool) {
      return false
    }
    return tickB <= pool.currentTick()
  }, [tickB, pool])

  // Tick adjustment handlers (+/- buttons)
  const handleMinPriceMinusClick = useCallback(() => {
    if (!pool) {
      return
    }

    let prevTick = undefined
    if (tickA === undefined) {
      prevTick = getInitializableTickIndex(pool.currentTick(), pool.tickSpacing)
    } else {
      prevTick = getPrevInitializableTickIndex(tickA, pool.tickSpacing)
    }
    if (tickB !== undefined && prevTick >= tickB) {
      prevTick = getPrevInitializableTickIndex(tickB, pool.tickSpacing)
    }

    setRangeMin(toDisplayPrice(pool.tickToPrice(prevTick), isReversed))
    setTickA(prevTick)
  }, [pool, tickA, tickB, isReversed])

  const handleMinPricePlusClick = useCallback(() => {
    if (!pool) {
      return
    }

    let nextTick = undefined
    if (tickA === undefined) {
      nextTick = getInitializableTickIndex(pool.currentTick(), pool.tickSpacing)
    } else {
      nextTick = getNextInitializableTickIndex(tickA, pool.tickSpacing)
    }
    if (tickB !== undefined && nextTick >= tickB) {
      nextTick = getPrevInitializableTickIndex(tickB, pool.tickSpacing)
    }

    setRangeMin(toDisplayPrice(pool.tickToPrice(nextTick), isReversed))
    setTickA(nextTick)
  }, [pool, tickA, tickB, isReversed])

  const handleMaxPriceMinusClick = useCallback(() => {
    if (!pool) {
      return
    }

    let prevTick = undefined
    if (tickB === undefined) {
      prevTick = getInitializableTickIndex(pool.currentTick(), pool.tickSpacing)
      if (prevTick < pool.currentTick()) {
        prevTick = getNextInitializableTickIndex(prevTick, pool.tickSpacing)
      }
    } else {
      prevTick = getPrevInitializableTickIndex(tickB, pool.tickSpacing)
    }
    if (tickA !== undefined && prevTick <= tickA) {
      prevTick = getNextInitializableTickIndex(tickA, pool.tickSpacing)
    }

    setRangeMax(toDisplayPrice(pool.tickToPrice(prevTick), isReversed))
    setTickB(prevTick)
  }, [pool, tickA, tickB, isReversed])

  const handleMaxPricePlusClick = useCallback(() => {
    if (!pool) {
      return
    }

    let nextTick = undefined
    if (tickB === undefined) {
      nextTick = getInitializableTickIndex(pool.currentTick(), pool.tickSpacing)
      if (nextTick < pool.currentTick()) {
        nextTick = getNextInitializableTickIndex(nextTick, pool.tickSpacing)
      }
    } else {
      nextTick = getNextInitializableTickIndex(tickB, pool.tickSpacing)
    }
    if (tickA !== undefined && nextTick <= tickA) {
      nextTick = getPrevInitializableTickIndex(tickA, pool.tickSpacing)
    }

    setRangeMax(toDisplayPrice(pool.tickToPrice(nextTick), isReversed))
    setTickB(nextTick)
  }, [pool, tickA, tickB, isReversed])

  // Chart tick handlers
  const handleTickAChange = useCallback(
    (tickA: number) => {
      if (!pool || tickA === undefined) {
        setRangeMin('')
        setTickA(undefined)
        return
      }

      setTickA(tickA)
      setRangeMin(toDisplayPrice(pool.tickToPrice(tickA), isReversed))
    },
    [isReversed, pool]
  )

  const handleTickBChange = useCallback(
    (tickB: number) => {
      if (!pool || tickB === undefined) {
        setRangeMax('')
        setTickB(undefined)
        return
      }

      setTickB(tickB)
      setRangeMax(toDisplayPrice(pool.tickToPrice(tickB), isReversed))
    },
    [isReversed, pool]
  )

  // Reset ticks when protocol changes
  const resetTicks = useCallback(() => {
    setTickA(undefined)
    setTickB(undefined)
  }, [])

  // Display-ready props for <PriceRange> — handles reversed pair swap internally
  // When reversed: min↔max are swapped, tick errors are swapped, handlers are cross-wired
  const priceRangeDisplayProps = isReversed
    ? {
        rangeMinRef: rangeMaxRef,
        rangeMaxRef: rangeMinRef,
        handleRangeMinChange: handleRangeMaxChange,
        handleRangeMaxChange: handleRangeMinChange,
        handleMinPriceMinusClick: handleMaxPricePlusClick,
        handleMinPricePlusClick: handleMaxPriceMinusClick,
        handleMaxPriceMinusClick: handleMinPricePlusClick,
        handleMaxPricePlusClick: handleMinPriceMinusClick,
        rangeMin: rangeMax,
        rangeMax: rangeMin,
        tickAError: tickBError,
        tickBError: tickAError,
      }
    : {
        rangeMinRef,
        rangeMaxRef,
        handleRangeMinChange,
        handleRangeMaxChange,
        handleMinPriceMinusClick,
        handleMinPricePlusClick,
        handleMaxPriceMinusClick,
        handleMaxPricePlusClick,
        rangeMin,
        rangeMax,
        tickAError,
        tickBError,
      }

  const currentPriceDisplay = pool ? toDisplayPrice(pool.currentPrice(), isReversed) : undefined

  const displaySymbolX = isReversed
    ? configInfo?.Y.displaySymbol || ''
    : configInfo?.X.displaySymbol || ''
  const displaySymbolY = isReversed
    ? configInfo?.X.displaySymbol || ''
    : configInfo?.Y.displaySymbol || ''

  return {
    // Raw values for computation hooks (useLeverageRange, usePositionCreationValues)
    tickA,
    tickB,

    // Display-ready props for <PriceRange> — already handles reversed pair
    priceRangeDisplayProps,
    currentPriceDisplay,
    displaySymbolX,
    displaySymbolY,

    // For <LiquidityChartRangeInput> (handles reversal internally via reverseTokens prop)
    handleTickAChange,
    handleTickBChange,

    // For protocol switch
    resetTicks,
  }
}
